import { apiClient } from './api';

export type DocumentType = 'estimate' | 'sales_order' | 'invoice' | 'delivery_challan';
export type PDFFormat = 'tally_prime' | 'thermal' | 'regular';
export type PrinterFormat = 'A4' | '80mm';

export interface GeneratePDFRequest {
  document_id: string;
  document_type: DocumentType;
  format?: PDFFormat;
  printer_format?: PrinterFormat;
}

export interface SendEmailRequest {
  document_id: string;
  document_type: DocumentType;
  recipient_email: string;
  subject?: string;
  message?: string;
}

export interface SendWhatsAppRequest {
  document_id: string;
  document_type: DocumentType;
  recipient_phone: string;
  message?: string;
}

export interface DocumentServiceResponse {
  success: boolean;
  message: string;
  message_id?: string;
}

class DocumentService {
  private getDocumentEndpoint(documentType: DocumentType): string {
    const endpoints: Record<DocumentType, string> = {
      estimate: '/admin/estimates',
      sales_order: '/admin/sales-orders',
      invoice: '/admin/invoices',
      delivery_challan: '/admin/delivery-challans'
    };
    return endpoints[documentType];
  }

  async generatePDF(request: GeneratePDFRequest): Promise<Blob> {
    const endpoint = this.getDocumentEndpoint(request.document_type);
    const params = new URLSearchParams();

    // Only the invoice PDF route accepts a query param, and it's named
    // "template" (regular/thermal/tally_prime) — estimate/sales_order/
    // delivery_challan routes take no query params at all.
    if (request.document_type === 'invoice' && request.format) {
      params.append('template', request.format);
    }

    const queryString = params.toString();
    const url = `${endpoint}/${request.document_id}/pdf${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'blob'
    });
    
    return response.data;
  }

  async sendEmail(request: SendEmailRequest): Promise<DocumentServiceResponse> {
    const endpoint = this.getDocumentEndpoint(request.document_type);
    
    const response = await apiClient.post(`${endpoint}/${request.document_id}/send-email`, {
      recipient_email: request.recipient_email,
      subject: request.subject,
      message: request.message
    });
    
    return response.data;
  }

  async sendWhatsApp(request: SendWhatsAppRequest): Promise<DocumentServiceResponse> {
    const endpoint = this.getDocumentEndpoint(request.document_type);
    
    const response = await apiClient.post(`${endpoint}/${request.document_id}/send-whatsapp`, {
      recipient_phone: request.recipient_phone,
      message: request.message
    });
    
    return response.data;
  }

  async printDocument(request: GeneratePDFRequest): Promise<void> {
    const pdfBlob = await this.generatePDF(request);
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    // Open in new window for printing
    const printWindow = window.open(pdfUrl, '_blank');
    
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        // Clean up the object URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 1000);
      };
    } else {
      // Fallback: download if popup blocked
      this.downloadPDF(pdfBlob, `document_${request.document_id}.pdf`);
      URL.revokeObjectURL(pdfUrl);
    }
  }

  async saveDocument(request: GeneratePDFRequest, filename?: string): Promise<void> {
    const pdfBlob = await this.generatePDF(request);
    const defaultFilename = filename || `${request.document_type}_${request.document_id}.pdf`;
    this.downloadPDF(pdfBlob, defaultFilename);
  }

  private downloadPDF(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const documentService = new DocumentService();
